import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { qk } from "@/api/query-keys";

/** 設計画面に並べるプロセスモデル。 */
export type DesignModel = {
  readonly processModelId: string;
  readonly displayName: string;
  readonly description: string | null;
  /** 所属グループの階層（表示名つき）。階層表示と検索に使う。 */
  readonly groupPath: readonly {
    readonly id: string;
    readonly displayName: string;
  }[];
};

type SpiffProcessModelRow = {
  readonly id: string;
  readonly display_name: string;
  readonly display_order?: number | null;
  readonly description?: string | null;
  readonly parent_groups?: readonly {
    readonly id?: string | null;
    readonly display_name?: string | null;
  }[];
};

/**
 * 設計対象のプロセスモデル一覧。
 *
 * 起票カタログ（use-runnable-models）と違い `filter_runnable_by_user` を付けない。
 * 設計者は起票権限を持たないモデルも見る必要があるため。
 */
export function designModelsQueryOptions(client: ApiClient) {
  const fetchModels = async (): Promise<readonly DesignModel[]> => {
    const response = await client.get<{
      readonly results?: readonly SpiffProcessModelRow[];
    }>("/process-models", {
      query: {
        per_page: 100,
        page: 1,
        recursive: true,
        // グループの表示名を取る（階層表示・グループ名での検索に使う）
        include_parent_groups: true,
      },
    });

    return (response.results ?? [])
      .map((row) => ({
        processModelId: row.id,
        displayName: row.display_name,
        description:
          row.description === undefined || row.description === ""
            ? null
            : row.description,
        groupPath: (row.parent_groups ?? [])
          .filter((parent) => typeof parent.id === "string" && parent.id !== "")
          .map((parent) => ({
            id: parent.id as string,
            displayName:
              parent.display_name === undefined ||
              parent.display_name === null ||
              parent.display_name === ""
                ? (parent.id as string)
                : parent.display_name,
          })),
        sortKey: row.display_order ?? 0,
      }))
      .sort(
        (a, b) =>
          a.sortKey - b.sortKey ||
          (a.processModelId < b.processModelId ? -1 : 1),
      )
      .map(({ sortKey: _sortKey, ...model }) => model);
  };

  return queryOptions({
    queryKey: qk.processModels.designList(),
    queryFn: fetchModels,
    staleTime: 5 * 60_000,
  });
}

export function useDesignModels(): UseQueryResult<readonly DesignModel[]> {
  return useQuery(designModelsQueryOptions(useApiClient()));
}
